import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Minus, Plus, ChevronLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useCart } from "../context/CartContext";
import { formatINR } from "../lib/api";
import SafeImage from "../components/SafeImage";

const VALID_CODES = { WELCOME10: 10, FESTIVE15: 15 };

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const navigate = useNavigate();

  const applyCoupon = () => {
    const code = coupon.trim().toUpperCase();
    const pct = VALID_CODES[code];
    if (pct) {
      setDiscount(pct);
      toast.success(`Coupon ${code} applied — ${pct}% off`);
    } else {
      setDiscount(0);
      toast.error("That coupon isn't valid. Try WELCOME10 or FESTIVE15.");
    }
  };

  const discountAmt = Math.round((subtotal * discount) / 100);
  const total = subtotal - discountAmt;

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="cart-empty">
        <p className="eyebrow">Your Cart</p>
        <h1 className="font-serif-display text-5xl md:text-6xl mt-4">It's quiet here.</h1>
        <p className="text-[#4A4A4A] mt-6 max-w-md mx-auto">
          Slow fashion rewards the patient. Start with a piece that speaks softly.
        </p>
        <Link to="/shop">
          <Button className="mt-10 bg-[#1A1A1A] hover:bg-[#000000] text-[#c3ba9e] rounded-none px-12 py-6 tracking-[0.22em] uppercase text-xs btn-shimmer">
            Explore the Collection
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20" data-testid="cart-page">
      <Link to="/shop" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#4A4A4A] hover:text-[#1A1A1A] mb-6">
        <ChevronLeft size={14} /> Continue shopping
      </Link>
      <div className="mb-12">
        <p className="eyebrow">Your Cart</p>
        <h1 className="font-serif-display text-4xl md:text-6xl mt-3">
          {items.length} {items.length === 1 ? "piece" : "pieces"} selected
        </h1>
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-12 md:gap-20">
        {/* Items list */}
        <div className="border-t border-[#a89e80]">
          {items.map((item) => (
            <div
              key={item.key}
              className="grid grid-cols-[100px_1fr] md:grid-cols-[120px_1fr_auto] gap-5 md:gap-8 py-6 border-b border-[#a89e80] items-start"
              data-testid={`cart-row-${item.id}`}
            >
              <Link to={`/shop/${item.slug}`} className="block">
                <SafeImage src={item.image} alt={item.name} className="w-full aspect-[3/4] object-cover bg-[#b8af92]" />
              </Link>
              <div className="min-w-0">
                <Link to={`/shop/${item.slug}`}>
                  <h3 className="font-serif-display text-2xl leading-tight hover:text-[#1A1A1A]">
                    {item.name}
                  </h3>
                </Link>
                <p className="text-xs text-[#7A7A7A] mt-1 tracking-wider">
                  {item.color} · Size {item.size}
                </p>
                <p className="text-sm mt-3 md:hidden">{formatINR(item.price * item.qty)}</p>
                <div className="flex items-center gap-4 mt-4">
                  <div className="flex items-center border border-[#a89e80]">
                    <button onClick={() => updateQty(item.key, item.qty - 1)} className="px-2 py-1.5 hover:bg-[#b8af92]" aria-label="Decrease">
                      <Minus size={12} />
                    </button>
                    <span className="px-3 text-sm w-8 text-center">{item.qty}</span>
                    <button onClick={() => updateQty(item.key, item.qty + 1)} className="px-2 py-1.5 hover:bg-[#b8af92]" aria-label="Increase">
                      <Plus size={12} />
                    </button>
                  </div>
                  <button
                    onClick={() => removeItem(item.key)}
                    className="text-[11px] tracking-[0.22em] uppercase text-[#7A7A7A] hover:text-[#1A1A1A] flex items-center gap-1"
                    data-testid={`cart-remove-${item.id}`}
                  >
                    <X size={12} /> Remove
                  </button>
                </div>
              </div>
              <p className="hidden md:block font-body text-base text-right shrink-0">
                {formatINR(item.price * item.qty)}
              </p>
            </div>
          ))}
        </div>

        {/* Summary */}
        <aside className="bg-[#b8af92] p-8 md:p-10 h-fit lg:sticky lg:top-28" data-testid="cart-summary">
          <h2 className="font-serif-display text-3xl mb-6">Order Summary</h2>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-[#4A4A4A]">Subtotal</span><span>{formatINR(subtotal)}</span></div>
            {discount > 0 && (
              <div className="flex justify-between text-[#1A1A1A]">
                <span>Discount ({discount}%)</span>
                <span>−{formatINR(discountAmt)}</span>
              </div>
            )}
            <div className="flex justify-between"><span className="text-[#4A4A4A]">Shipping</span><span className="text-[#1A1A1A]">Complimentary</span></div>
            <div className="flex justify-between"><span className="text-[#4A4A4A]">Estimated GST</span><span>Included</span></div>
          </div>

          <div className="border-t border-[#a89e80] mt-5 pt-5 flex justify-between items-baseline">
            <span className="eyebrow">Total</span>
            <span className="font-serif-display text-3xl" data-testid="cart-total">{formatINR(total)}</span>
          </div>

          {/* Coupon */}
          <div className="mt-8">
            <p className="eyebrow mb-3">Promo code</p>
            <div className="flex gap-2">
              <Input
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                placeholder="WELCOME10"
                className="rounded-none border-[#a89e80] bg-[#c3ba9e] text-sm"
                data-testid="cart-coupon-input"
              />
              <Button onClick={applyCoupon} variant="outline" className="rounded-none border-[#1A1A1A] text-xs tracking-[0.22em] uppercase px-5" data-testid="cart-coupon-apply">
                Apply
              </Button>
            </div>
            <p className="text-[11px] text-[#7A7A7A] mt-2">Try WELCOME10 or FESTIVE15</p>
          </div>

          <Button
            onClick={() => navigate("/checkout")}
            className="w-full mt-8 bg-[#1A1A1A] hover:bg-[#000000] text-[#c3ba9e] rounded-none py-6 tracking-[0.22em] uppercase text-xs btn-shimmer"
            data-testid="cart-checkout-button"
          >
            Proceed to Checkout
          </Button>
          <p className="text-[11px] text-[#7A7A7A] mt-4 text-center tracking-wider">
            UPI · Cards · Wallets · COD supported at checkout
          </p>
        </aside>
      </div>
    </div>
  );
}
